import type { DashboardOrder, OrderDetails } from '../model'
import { useMemo } from 'react'
import { useClientConfig } from '@/entities/client-config/lib'
import { useStateConfigErrorStates } from '@/entities/state/model'
import { useRealtimeEnabled, useRealtimeLastEventTime, useRealtimeOrders } from '@/features/realtime/lib'
import { filterOrdersByColumns } from './order.filter'
import { sortOrdersByArrivalTime, sortOrdersByDashboardRules } from './order.sort'

/**
 * Returns orders filtered and sorted by the dashboard column configuration
 *
 * @param dashboardOrders - Record of orders grouped by state category
 * @returns Record of filtered and sorted orders that match the dashboard column configuration
 */
export function useFilteredOrdersByDashboardColumns(
  dashboardOrders: Record<string, DashboardOrder[]>,
): Record<string, DashboardOrder[]> {
  const { config: { layout } } = useClientConfig()
  const { data: errorStates } = useStateConfigErrorStates()

  return useMemo(() => {
    const filtered = filterOrdersByColumns(dashboardOrders, layout.middle.columns)
    for (const columnKey in filtered) {
      if (columnKey === 'Arrived') {
        // Arrived uses stable sorting with lastTransitionTime to prevent acknowledged orders from moving down
        filtered[columnKey] = sortOrdersByArrivalTime(filtered[columnKey])
      } else {
        // All other columns use dashboard rules: ETA-based sorting with lastUpdateTime for accurate time-until-arrival
        filtered[columnKey] = sortOrdersByDashboardRules(filtered[columnKey], errorStates || [])
      }
    }
    return filtered
  }, [dashboardOrders, layout.middle.columns, errorStates])
}

/**
 * Returns the effective orders array based on tutorial mode, realtime, and polled data.
 *
 * This function implements a polling fallback logic to ensure that the application
 * always displays the most up-to-date orders. Here's how it works:
 *
 * 1. If the application is in tutorial mode, it returns the polled orders.
 * 2. If realtime data is enabled and available, and the last realtime event time is
 *    newer than the last poll time, it returns the realtime orders.
 * 3. Otherwise, it returns the polled orders.
 *
 * The function uses memoization to prevent unnecessary recalculations of the
 * effective orders array. The memoization is based on the polled orders, realtime orders,
 * and timestamp comparisons, which ensures that the function returns the same result as long
 * as the input data hasn't changed.
 *
 * @param polledOrders Orders from React Query polling
 * @param polledUpdatedAt Timestamp of last poll (milliseconds)
 */
export function useEffectiveOrders(polledOrders: OrderDetails[], polledUpdatedAt: number | undefined) {
  const realtimeOrders = useRealtimeOrders()
  const useRealtimeData = useRealtimeEnabled()
  const lastRealtimeEventTime = useRealtimeLastEventTime()
  const isTutorialMode = typeof globalThis.window !== 'undefined' && globalThis.window.__tutorialMode

  const lastPollMs = polledUpdatedAt ?? 0

  const lastRealtimeMs = useMemo(() => {
    return Date.parse(lastRealtimeEventTime || '') || 0
  }, [lastRealtimeEventTime])

  // Check if we should use the realtime orders based on the polling fallback logic.
  // Compare timestamps numerically: if the last realtime event time is newer than the last poll time, use the realtime orders.
  const shouldUseRealtime = useMemo(() => {
    return !isTutorialMode
      && useRealtimeData
      && realtimeOrders.length > 0
      && lastRealtimeMs > lastPollMs
  }, [isTutorialMode, useRealtimeData, realtimeOrders, lastRealtimeMs, lastPollMs])

  // Return the effective orders array based on the polling fallback logic.
  // If shouldUseRealtime is true, return the realtime orders; otherwise, return the polled orders.
  return useMemo(() => {
    return shouldUseRealtime ? realtimeOrders : polledOrders
  }, [shouldUseRealtime, realtimeOrders, polledOrders])
}
